/**
 * Feature E04-F03 — "Email Checker (BullMQ worker)" request/response contracts.
 *
 * 1:1 port of the legacy n8n "AIAC EER Email Checker" workflow
 * (docs/source-n8n/reference/EmailChecker.md). That workflow periodically (and on
 * demand from the SPA) fetches new inbound mail for every active mailbox, classifies
 * it with pure rules (regex / domain / contact — NO LLM), deduplicates by
 * external_id (Message-ID) and persists rows into `emails` (direction='in',
 * project_id ALWAYS null — no auto-linking to a project).
 *
 * Single source of truth for:
 *   - POST /inbox/check          manual-trigger request/response (T06)
 *     (parity: Webhook `aiac-eer/check-mail-trigger` + Respond to Webhook node —
 *      `{ success, message, timestamp }`).
 *   - the classification vocabulary + the shape of a classified email and its
 *     `meta` blob written into `emails.meta` (T03/T05).
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// T06 — POST /inbox/check (manual trigger)
// ---------------------------------------------------------------------------
/**
 * Manual-trigger request body. Parity with the legacy webhook body
 * `{ userId, email, session_token }`:
 *   - `userId` optional — when present only that user's active mailbox is
 *     checked (`WHERE user_id=:userId AND is_active=true`); when absent ALL
 *     active mailboxes are processed (Schedule Trigger parity).
 *
 * NB (security note carried over from the source, see EmailChecker.md gotchas):
 * the legacy webhook did NOT authenticate the request and trusted body.userId
 * verbatim (IDOR + SQL injection). In v2 the route is protected by
 * `app.authenticate` and `userId` is passed as a *parameterized* value to Prisma
 * — never string-interpolated. `email` / `session_token` from the legacy body are
 * accepted-but-ignored (the session is the identity).
 */
export const inboxCheckRequestSchema = z
    .object({
    userId: z.string().min(1).max(50).optional(),
    /** Accepted for legacy parity, ignored by the handler. */
    email: z.string().optional(),
    /** Accepted for legacy parity, ignored by the handler (no body auth). */
    session_token: z.string().nullable().optional(),
})
    .strip();
/**
 * Manual-trigger response — verbatim shape of the legacy Respond to Webhook node
 * (`Return Summary`): `{ success:true, message, timestamp }`. The legacy summary
 * is static (no counts); v2 keeps `success`/`message`/`timestamp` for parity and
 * ADDS optional counters (processed mailboxes / saved emails) that the SPA can
 * surface — the legacy SPA simply ignored extra fields.
 */
export const inboxCheckResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    message: z.string(),
    /** ISO 8601 timestamp (`new Date().toISOString()` — Return Summary parity). */
    timestamp: z.string(),
    /**
     * BullMQ job id of the enqueued one-off check (v2 addition; present ONLY on the
     * async queued path). The SPA polls `GET /inbox/check/:jobId/status` with this id
     * to learn when the worker actually finished (the enqueue ack is instant, the
     * fetch/classify/save loop is not). Absent on the synchronous no-Redis fallback
     * (there the work is already done when the response arrives).
     */
    jobId: z.string().optional(),
    /** Number of mailboxes processed in this run (v2 addition; optional). */
    mailboxesProcessed: z.number().int().nonnegative().optional(),
    /** Number of new (non-duplicate) emails saved (v2 addition; optional). */
    emailsSaved: z.number().int().nonnegative().optional(),
    /** Number of mailboxes whose fetch/processing failed (v2 addition; optional). */
    mailboxesFailed: z.number().int().nonnegative().optional(),
});
// ---------------------------------------------------------------------------
// T09 — GET /inbox/check/:jobId/status (completion indicator poll)
// ---------------------------------------------------------------------------
/**
 * Lifecycle states of a manual-check BullMQ job, as reported by `job.getState()`.
 *
 * The active-ish states (`waiting` … `paused`) mean the worker has not finished;
 * the SPA keeps polling. The terminal states (`completed` / `failed` / `unknown`)
 * stop the poll — `unknown` covers a job that has already been auto-cleaned after
 * completing (the manual job is `removeOnComplete` after a short retention window)
 * or an id that never existed; either way there is nothing left to wait for.
 */
export const INBOX_CHECK_JOB_STATUSES = [
    'waiting',
    'active',
    'delayed',
    'prioritized',
    'waiting-children',
    'paused',
    'completed',
    'failed',
    'unknown',
];
export const inboxCheckJobStatusSchema = z.enum(INBOX_CHECK_JOB_STATUSES);
/** Statuses at which the SPA stops polling (nothing more will happen). */
export const TERMINAL_INBOX_CHECK_STATUSES = ['completed', 'failed', 'unknown'];
/** True when polling should stop (job finished, failed, or is gone). */
export function isTerminalInboxCheckStatus(status) {
    return TERMINAL_INBOX_CHECK_STATUSES.includes(status);
}
/**
 * `GET /inbox/check/:jobId/status` response — the current lifecycle state of a
 * previously-enqueued manual check. `jobId` echoes the requested id so a stale
 * poll can be correlated.
 */
export const inboxCheckStatusResponseSchema = z.object({
    jobId: z.string(),
    status: inboxCheckJobStatusSchema,
});
// ---------------------------------------------------------------------------
// T03 — classification vocabulary
// ---------------------------------------------------------------------------
/**
 * The two classification values the legacy `Filter & Classify` node assigns.
 *   - `unclassified`         — default; also kept for emails of a known
 *                              contact/client (the match is info-only in meta).
 *   - `new_project_candidate`— set ONLY when no client matched AND the sender
 *                              contains 'sales@eer'/'@eerglobal' OR the subject
 *                              matches the new-project regex.
 *
 * IMPORTANT (source invariant): there is NO LLM here and NO auto-linking to a
 * project — `project_id` is always null at import.
 */
export const EMAIL_CLASSIFICATIONS = ['unclassified', 'new_project_candidate'];
export const emailClassificationSchema = z.enum(EMAIL_CLASSIFICATIONS);
/**
 * Regex from the legacy node: subject → new_project_candidate trigger.
 *
 * The `#new_project` hashtag is matched via the bare `new_project` token: a
 * leading `\b#new_project` never matched because `\b` requires a word/non-word
 * boundary and `#` is itself a non-word char, so `space#` has no boundary before
 * `#`. Matching `new_project` (with its `_` being a word char) catches both
 * `#new_project` (the `\b` sits between `#` and `n`) and a bare `new_project`,
 * while `\b` on both ends still keeps non-triggers like "status update" out.
 */
export const NEW_PROJECT_SUBJECT_REGEX = /\b(new project|new_project|new vacancy|intro)\b/i;
/** Sender substrings (lowercase) that flag a new-project candidate. */
export const NEW_PROJECT_SENDER_MARKERS = ['sales@eer', '@eerglobal'];
/** First-run IMAP `SINCE` default when a mailbox has never been checked. */
export const DEFAULT_SINCE_DATE = '2020-01-01T00:00:00Z';
/** Per-fetch hard cap (legacy `$top=50` / `limit:50`). */
export const FETCH_LIMIT = 50;
// ---------------------------------------------------------------------------
// T03/T05 — classified email + its persisted meta blob
// ---------------------------------------------------------------------------
/** One attachment descriptor (unified IMAP/Graph shape). */
export const emailAttachmentSchema = z.object({
    filename: z.string(),
    contentType: z.string().optional(),
    size: z.number().int().nonnegative().optional(),
});
/**
 * The `emails.meta` jsonb blob the legacy node writes for an inbound email.
 * `matched_project_id` is ALWAYS null (no auto-link); matched_contact_id /
 * matched_client_id are info-only hints. account_* / user_id record which mailbox
 * the message came from.
 */
export const emailMetaSchema = z.object({
    classification: emailClassificationSchema,
    matched_project_id: z.null(),
    matched_contact_id: z.number().int().nullable(),
    matched_client_id: z.number().int().nullable(),
    account_id: z.number().int().nullable(),
    account_email: z.string().nullable(),
    user_id: z.string().nullable(),
    attachments: z.array(emailAttachmentSchema),
});
/**
 * A classified, non-duplicate inbound email ready to persist (output of
 * `Filter & Classify`). `project_id` is always null (no auto-link); `direction`
 * is always 'in'; `external_id` is the Message-ID used for dedup.
 */
export const classifiedEmailSchema = z.object({
    project_id: z.null(),
    sender: z.string(),
    sender_name: z.string().nullable(),
    recipient: z.string().nullable(),
    subject: z.string().nullable(),
    body: z.string().nullable(),
    direction: z.literal('in'),
    thread_id: z.string().nullable(),
    external_id: z.string().nullable(),
    /** ISO 8601 received timestamp (email.date || now). */
    received_at: z.string(),
    meta: emailMetaSchema,
});
//# sourceMappingURL=inbox-check.js.map